import { HttpClient, HttpParams } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { HardwareSource } from "@interfaces/IAccumulatedData";
import { TranslationService } from "@services/translation.service";
import { MultiStateInputCategory } from "@temperature/_enums/reefer/MultiStateInputCategory.enum";
import { ReeferZoneOperatingModeCategory } from "@temperature/_enums/reefer/ReeferZoneOperatingModeCategory.enum";
import { IMultiStateInput } from "@temperature/_models/multiStateInputs/IMultiStateInput";
import { IReeferWithMappings } from "@temperature/_models/reefer/IReeferWithMappings";
import { ITemperatureProbeWithTemperatureData } from "@temperature/_models/temperature/ITemperatureProbe";
import { ITemperatureRecord } from "@temperature/_models/temperature/ITemperatureRecord";
import { ITemperatureSetpoint } from "@temperature/_models/temperature/ITemperatureSetpoint";
import { IZone, IZoneWithProbes } from "@temperature/_models/temperature/ITemperatureZone";
import { IZoneTemperatureOutOfBoundsHistory } from "@temperature/_models/temperature/ITemperatureZoneOutOfBoundsHistory";
import { IReeferZoneMode } from "@temperature/_models/zones/IReeferZoneMode";
import { IZoneQueryOptions } from "@temperature/_models/zones/IZoneQueryOptions";
import { ZoneTemperatureOutOfBoundsHistoryQueryOptions } from "@temperature/_models/zones/IZoneTemperatureOutOfBoundsHistoryQueryOptions";
import { Observable } from "rxjs";
import { IMultiStateInputRecord } from "../_models/multiStateInputs/IMultiStateInputRecord";

@Injectable({
    providedIn: 'root'
})
export class ZoneService {
    constructor(
        private readonly http: HttpClient,
        private readonly translationService: TranslationService) {

    }

    getZones(queryOptions: IZoneQueryOptions = {}): Observable<IZone[]> {
        return this.http.get<IZone[]>(`/api/zones`, { params: queryOptions as HttpParams });
    }

    getZonesTemperatureOutOfBoundsHistory(queryOptions: ZoneTemperatureOutOfBoundsHistoryQueryOptions = {}) {
        return this.http.get<IZoneTemperatureOutOfBoundsHistory[]>('/api/zones/temperature/outofboundshistory', { params: queryOptions as HttpParams });
    }
    saveTemperatureZone(zone: IZone): Observable<IZone> {
        return this.http.post<IZone>('/api/zones', zone)
    }

    getLatestReeferZoneOperatingModes(latestMultiStateInputRecords: IMultiStateInputRecord[], multiStateInputs: IMultiStateInput[], source: HardwareSource): IReeferZoneMode[] {
        if (!latestMultiStateInputRecords || !latestMultiStateInputRecords.length) {
            return;
        }

        const inputs = multiStateInputs.filter(input => input.Category === MultiStateInputCategory.ReeferZoneOperatingMode && input.Source === source);

        const statusesByZone: IReeferZoneMode[] = [];
        inputs.forEach(input => {
            const linkedRecord = latestMultiStateInputRecords.find(history => {
                return history.MultiStateInputId === input.Id;
            })
            if (linkedRecord) {
                const triggerStateItem = {
                    zoneId: input.ZoneId,
                    status: linkedRecord.State,
                    latestRecord: linkedRecord
                };
                statusesByZone.push(triggerStateItem);
            }
        })

        return statusesByZone;
    }

    mapZoneStatuses(zones: IZoneWithProbes[], reeferZoneModes: IReeferZoneMode[]) {
        if (!zones?.length || !reeferZoneModes?.length) {
            return;
        }
        zones.forEach(zone => {
            if (!reeferZoneModes || !reeferZoneModes.length) {
                zone.operatingMode = -1;
            } else {
                var reeferZoneMode = reeferZoneModes.find(mode => mode.zoneId === zone.Id);
                zone.operatingMode = reeferZoneMode?.status;
                zone.zoneOperatingMode = reeferZoneMode
            }
        })
    }

    mapProbesToZone(zones: IZoneWithProbes[], probes: ITemperatureProbeWithTemperatureData[]): IZoneWithProbes[] {
        if (!zones || !probes) {
            return;
        }

        zones.forEach(zone => {
            zone.probes = probes.filter(probe => probe.ZoneId === zone.Id);
        });

        return zones;
    }

    mapTemperatureSetpointsToZones(zones: IZoneWithProbes[], temperatureSetpoints: ITemperatureSetpoint[]) {
        if (!zones || !temperatureSetpoints) {
            return;
        }
        zones.forEach(zone => {
            zone.setpoint = temperatureSetpoints.find(setpoint => setpoint.ZoneId === zone.Id);
            zone.setpointReading = zone.setpoint?.histories[0]?.SetpointValue;
        })
    }

    mapOutOfBoundsHistoryToZones(zones: IZoneWithProbes[], histories: IZoneTemperatureOutOfBoundsHistory[]) {
        zones.forEach(zone => {
            zone.activeOutOfBoundsHistory = histories.filter(history => history.ZoneId === zone.Id && !history.EndTime);
            if (zone.activeOutOfBoundsHistory.length > 0) {
                zone.outOfBounds = true;
            }
        })
    }

    hasZoneOutOfBounds(reeferUnit: IReeferWithMappings): boolean {
        if (!reeferUnit.zones || !reeferUnit.zones.length) {
            return false;
        }

        return reeferUnit.zones.some(zone => {
            return zone.outOfBounds;
        })
    }

    getOperatingModeIcon(category: ReeferZoneOperatingModeCategory) {
        if (category == undefined) {
            return undefined;
        }

        switch (category) {
            case ReeferZoneOperatingModeCategory.Heating:
                return 'local_fire_department';

            case ReeferZoneOperatingModeCategory.Cooling:
                return 'ac_unit';

            case ReeferZoneOperatingModeCategory.Defrost:
                return 'water_drop';

            case ReeferZoneOperatingModeCategory.Idle:
                return 'hourglass_empty';

            case ReeferZoneOperatingModeCategory.Sleep:
                return 'bedtime';

            default:
            case ReeferZoneOperatingModeCategory.Unknown:
            case ReeferZoneOperatingModeCategory.PowerOff:
                return 'power_settings_new';

            case ReeferZoneOperatingModeCategory.Pretrip:
                return 'timer';
        }
    }

    getOperatingModeTranslation(operatingMode: ReeferZoneOperatingModeCategory): string {
        if (!operatingMode || operatingMode === ReeferZoneOperatingModeCategory.Unknown) {
            return this.translationService.tEnum(ReeferZoneOperatingModeCategory, "ReeferZoneOperatingModeCategory", ReeferZoneOperatingModeCategory.PowerOff)
        }

        return this.translationService.tEnum(ReeferZoneOperatingModeCategory, "ReeferZoneOperatingModeCategory", operatingMode);
    }

    getOperatingModeColour(category: ReeferZoneOperatingModeCategory) {
        switch (category) {
            case ReeferZoneOperatingModeCategory.Cooling:
                return "#8AC2E6";

            case ReeferZoneOperatingModeCategory.Heating:
                return "#cf4946";

            default:
                return "rgba(0, 0, 0, 0.54)";
        }
    }

    getOperatingModeColourClass(category: ReeferZoneOperatingModeCategory): string {
        if (!category) {
            return 'op-mode-unknown';
        }

        const className = ReeferZoneOperatingModeCategory[category]?.toLowerCase();
        return "op-mode-" + className;
    }

    mapRecordsToZones(zones: IZoneWithProbes[], temperatureRecords: ITemperatureRecord[]): void {
        if (!zones || !temperatureRecords) {
            return;
        }

        zones.forEach(zone => {
            zone.temperatureRecords = temperatureRecords.filter(rec => rec.Position === zone.Position);
        });
    }

    getZoneAtPosition(zones: IZoneWithProbes[], position: number) {
        if (!zones?.length) {
            return;
        }

        return zones.find(zone => zone.Position === position);
    }

    setZonesOperatingMode(zones: IZoneWithProbes[]) {
        if (!zones || !zones.length) {
            return;
        }
        zones.forEach((zone: IZoneWithProbes) => {
            zone.zoneIcon = this.getOperatingModeIcon(zone.operatingMode);
            zone.zoneIconClass = this.getOperatingModeColourClass(zone.operatingMode);
        });
    }

}
